import cn from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { TextButton } from '@bibliocommons/base-buttons';
import { Link } from '@bibliocommons/base-links';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { userIdentityShape } from '@bibliocommons/bc-prop-types';
import UserAvatar from '@bibliocommons/user-avatar';
import UserBadge from '@bibliocommons/user-badge';

import UserCardPopover from './UserCardPopover';

import './UserCard.scss';

const UserCard = ({
  hasAvatar,
  hasBadge,
  hasLibraryFullName,
  hasSmallUserName,
  isFollowing,
  isIgnoring,
  isMobileApp,
  isSyncingFollowForTargetUser,
  isSyncingIgnoreForTargetUser,
  overrideScreenReaderMessageUserName,
  toggleIsFollowing,
  toggleIsIgnoring,
  trackProfileLinkClick,
  ugcType,
  user
}) => {
  const { active: isActive, id: userId, homeLibraryFullName, name, webUrl } = user?.toJS();

  const profileUrl = webUrl || `/user_profile/${userId}`;
  const hasProfileLink = !isMobileApp && Boolean(profileUrl);
  const handleProfileLinkClick = linkText => {
    if (typeof trackProfileLinkClick === 'function') {
      trackProfileLinkClick({ linkText });
    }
  };

  const userNameClass = cn('user-name', { 'user-name--small': hasSmallUserName });

  return (
    <div className="cp-core-user-card">
      {hasAvatar && (
        <div className="avatar-container">
          <UserAvatar hideLink={!hasProfileLink} size="xs" userIdentity={user} />
        </div>
      )}
      <div className="details">
        <div className="name-and-badge">
          <div className="popover-container">
            {!isActive && name}
            {isActive && (
              <UserCardPopover
                isFollowing={isFollowing}
                isIgnoring={isIgnoring}
                isMobileApp={isMobileApp}
                isSyncingFollowingState={isSyncingFollowForTargetUser}
                isSyncingIgnoringState={isSyncingIgnoreForTargetUser}
                offset={[0, -4]}
                toggleIsFollowing={toggleIsFollowing}
                toggleIsIgnoring={toggleIsIgnoring}
                userIdentity={user}
                handleProfileLinkClick={handleProfileLinkClick}
              >
                {hasProfileLink ? (
                  <Link
                    className={userNameClass}
                    dataKey="cp-core-user-card-profile-link"
                    href={profileUrl}
                    handleClick={() => handleProfileLinkClick(name)}
                  >
                    <span aria-hidden>{name}</span>
                    <ScreenReaderMessage>
                      {overrideScreenReaderMessageUserName || (
                        <FormattedMessage id="sr_view_profile" values={{ username: name }} />
                      )}
                    </ScreenReaderMessage>
                  </Link>
                ) : (
                  <TextButton className="popover-trigger" dataKey="cp-core-user-card-popover-trigger">
                    <span aria-hidden className={userNameClass}>
                      {name}
                    </span>
                    <ScreenReaderMessage>
                      <FormattedMessage
                        defaultMessage={`Actions for ${name}'s profile, expands popover`}
                        id="sr_view_user_card"
                        values={{ username: name }}
                      />
                    </ScreenReaderMessage>
                  </TextButton>
                )}
              </UserCardPopover>
            )}
          </div>
          <UserBadge displayStaffBadge={hasBadge} ugcType={ugcType} userIdentity={user} />
        </div>
        {hasLibraryFullName && (
          <>
            <div aria-hidden className="home-library">
              {homeLibraryFullName}
            </div>
            <ScreenReaderMessage>
              <FormattedMessage id="sr_library_user" values={{ libraryName: homeLibraryFullName }} />
            </ScreenReaderMessage>
          </>
        )}
      </div>
    </div>
  );
};

UserCard.defaultProps = {
  hasAvatar: false,
  hasBadge: false,
  hasLibraryFullName: false,
  hasSmallUserName: false,
  isMobileApp: false
};

UserCard.propTypes = {
  hasAvatar: PropTypes.bool,
  hasBadge: PropTypes.bool,
  hasLibraryFullName: PropTypes.bool,
  hasSmallUserName: PropTypes.bool,
  isFollowing: PropTypes.bool,
  isIgnoring: PropTypes.bool,
  isMobileApp: PropTypes.bool,
  isSyncingFollowForTargetUser: PropTypes.bool,
  isSyncingIgnoreForTargetUser: PropTypes.bool,
  overrideScreenReaderMessageUserName: PropTypes.string,
  toggleIsFollowing: PropTypes.func.isRequired,
  toggleIsIgnoring: PropTypes.func.isRequired,
  trackProfileLinkClick: PropTypes.func,
  ugcType: PropTypes.oneOf(['comment', 'contentNotice', 'list', 'quotation', 'summary', 'video']).isRequired,
  user: userIdentityShape.isRequired
};

export default UserCard;
